import { Button, Input } from "@devhop/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { feedsApi } from "@/modules/cf-feed/presentation/api/client";
import type {
  WorkspaceCommand,
  WorkspaceSnapshot,
} from "../../domain/contracts/live-workspace";
import { liveApi } from "../api/client";
import type { ProductSet } from "../api/product-set-client";
import {
  workspaceErrorMessage,
  workspaceRequest,
} from "../api/workspace-client";
import { WorkspaceSelect } from "./WorkspaceProductEditor";
import {
  type ProductSetApply,
  type ProductSetApplyResult,
  WorkspaceProductSetReview,
} from "./WorkspaceProductSetReview";

/** Select an existing authorized target; the review/apply stays with the workspace owner. */
export function ProductSetDestination({
  template,
  disabled,
}: {
  template: ProductSet;
  disabled: boolean;
}) {
  const [kind, setKind] = useState("feed");
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState("");
  const [review, setReview] = useState(false);
  const retry = useRef<WorkspaceCommand | null>(null);
  const client = useQueryClient();
  const targets = useQuery({
    queryKey: ["product-set-destinations", template.pageId, kind, search],
    enabled: !disabled,
    queryFn: async () =>
      kind === "feed"
        ? (
            await feedsApi.list({
              pageId: template.pageId,
              feedType: "post",
              q: search || undefined,
              limit: 100,
            })
          ).feeds
            .filter(
              (feed) =>
                feed.sessionStatus !== "archived" &&
                feed.displayStatus !== "inactive",
            )
            .map((feed) => ({
              value: `feed/${feed.id}`,
              label: feed.title ?? feed.messagePreview ?? "Feed ບໍ່ມີຊື່",
            }))
        : (
            await liveApi.list({
              pageId: template.pageId,
              q: search || undefined,
              limit: 100,
            })
          ).items
            .filter((item) => item.sessionStatus !== "archived")
            .map((item) => ({
              value: `${item.kind}/${item.id}`,
              label: item.title,
            })),
  });
  const workspace = useQuery({
    queryKey: ["product-set-target-workspace", template.pageId, target],
    enabled: review && !disabled && Boolean(target),
    queryFn: async () => {
      const [kind, id] = target.split("/");
      const resolved = await workspaceRequest<{ id: string }>("/resolve", {
        kind,
        id,
      });
      const snapshot = await workspaceRequest<WorkspaceSnapshot>(
        `/${resolved.id}`,
      );
      if (snapshot.pageId !== template.pageId)
        throw new Error("PAGE_NOT_FOUND");
      return snapshot;
    },
  });
  const apply: ProductSetApply = async (templateId, preview) => {
    if (disabled || !workspace.data) return false;
    const action = {
      type: "apply-product-set" as const,
      templateId,
      templateRevision: preview.templateRevision,
      previewFingerprint: preview.fingerprint,
    };
    if (
      !retry.current ||
      JSON.stringify(retry.current.action) !== JSON.stringify(action) ||
      retry.current.revision !== preview.revision
    )
      retry.current = {
        key: crypto.randomUUID(),
        revision: preview.revision,
        action,
      };
    const result = await workspaceRequest<ProductSetApplyResult>(
      `/${workspace.data.id}/commands`,
      retry.current,
    );
    retry.current = null;
    await client.invalidateQueries({
      queryKey: ["workspace", workspace.data.id],
    });
    return result;
  };
  return (
    <section className="space-y-3 rounded-lg border p-4" aria-label="ນຳຊຸດໄປໃຊ້">
      <h2 className="font-semibold">ນຳຊຸດໄປໃຊ້ໃນ Feed / Live</h2>
      <p className="text-muted-foreground text-sm">
        {disabled ? "ບັນທຶກຊຸດກ່ອນເລືອກງານ" : "ເລືອກງານໃນເພຈດຽວກັນ ແລະກວດລາຄາຈິງກ່ອນເພີ່ມ"}
      </p>
      <fieldset disabled={disabled} className="space-y-3">
        <WorkspaceSelect
          label="ປະເພດງານ"
          value={kind}
          onChange={(value) => {
            setKind(value);
            setTarget("");
            setReview(false);
          }}
          items={[
            ["feed", "Feed"],
            ["live", "Live"],
          ]}
        />
        <label
          htmlFor="product-set-target-search"
          className="block space-y-1 text-sm"
        >
          <span>ຄົ້ນຫາງານ (ສະແດງສູງສຸດ 100 ງານ)</span>
          <Input
            id="product-set-target-search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setTarget("");
            }}
          />
        </label>
        {targets.isPending ? (
          <output className="block">ກຳລັງໂຫຼດງານ…</output>
        ) : targets.isError ? (
          <div role="alert">
            <p>{workspaceErrorMessage(targets.error)}</p>
            <Button variant="outline" onClick={() => void targets.refetch()}>
              ລອງໃໝ່
            </Button>
          </div>
        ) : targets.data?.length ? (
          <WorkspaceSelect
            label="ງານປາຍທາງ"
            value={target}
            onChange={(value) => {
              setTarget(value);
              setReview(false);
              retry.current = null;
            }}
            items={targets.data.map(
              (item) => [item.value, item.label] as [string, string],
            )}
          />
        ) : (
          <p>ບໍ່ພົບງານທີ່ໃຊ້ໄດ້</p>
        )}
        <Button
          variant="outline"
          disabled={disabled || !target || workspace.isFetching}
          onClick={() => {
            setReview(true);
            if (workspace.data || workspace.isError) void workspace.refetch();
          }}
        >
          ກວດລາຄາໃນງານນີ້
        </Button>
      </fieldset>
      {review && workspace.isPending ? (
        <output className="block">ກຳລັງໂຫຼດງານປາຍທາງ…</output>
      ) : null}
      {review && workspace.isError ? (
        <div role="alert">
          <p>{workspaceErrorMessage(workspace.error)}</p>
          <Button variant="outline" onClick={() => void workspace.refetch()}>
            ລອງໃໝ່
          </Button>
        </div>
      ) : null}
      {review && !disabled && workspace.data ? (
        <WorkspaceProductSetReview
          key={workspace.data.id}
          template={template}
          workspace={workspace.data}
          onApply={apply}
          onRefresh={() =>
            Promise.all([
              workspace.refetch(),
              client.invalidateQueries({
                queryKey: ["cf-product-sets", template.pageId],
              }),
            ])
          }
          onClose={() => {
            setReview(false);
            retry.current = null;
          }}
        />
      ) : null}
      <p className="text-muted-foreground text-xs">
        SKU ທີ່ມີແລ້ວຈະຂ້າມ · ການແກ້ຊຸດບໍ່ປ່ຽນງານເກົ່າອັດຕະໂນມັດ
      </p>
    </section>
  );
}
